import { useCallback, useLayoutEffect } from "react";
import { useWindowDimensions } from "react-native";
import Animated, { SlideInLeft, SlideInRight, SlideOutLeft, SlideOutRight } from "react-native-reanimated";
import { Stack, useRouter } from "expo-router";
import { HeaderBackButton } from "@react-navigation/elements";
import { useUnistyles } from "react-native-unistyles";
import { ClipCompilationPage, CompilationWatchView, RequestCompilationForm } from "@visioni/a3s-media/app";
import { DialogModal } from "@src/components/DialogModal";
import { useClipCompilationEnabled } from "@src/features/clip-compilation/hooks/useClipCompilationEnabled";
import { useClipCompilationPageProps } from "@src/features/clip-compilation/hooks/useClipCompilationPageProps";
import { useCompilationWatch } from "@src/features/clip-compilation/hooks/useCompilationWatch";
import { useIsFirstRender } from "@src/hooks/useIsFirstRender";
import { CompilationVideoPlayer } from "@src/features/clip-compilation/components/CompilationVideoPlayer";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";

const styles = { flex: { flex: 1 } } as const;

/**
 * Access gate. The screen's data hooks live in `ClipCompilationContent` so that
 * mounting them is itself gated — rendering the content and redirecting
 * afterwards would flash the restricted UI and fire the list, sites and camera
 * queries for a user on their way out.
 */
export default function ClipCompilationScreen() {
  const router = useRouter();
  const isEnabled = useClipCompilationEnabled();

  useLayoutEffect(() => {
    if (!isEnabled) {
      router.replace("/overview");
    }
  }, [isEnabled, router]);

  if (!isEnabled) return null;

  return <ClipCompilationContent />;
}

function ClipCompilationContent() {
  const { theme } = useUnistyles();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  // This screen is pushed from the Settings tab, outside the tenant Stack
  // navigator, so it's the first entry in that Stack's own history — the
  // default back button doesn't appear without this (see the hook's doc).
  const { headerLeft } = useHeaderBackButton("/settings");

  const { pageProps, isRequestDialogOpen, closeRequestDialog, requestFormProps, watchingItem, closeWatchView } =
    useClipCompilationPageProps();

  const { clip, handleDownload } = useCompilationWatch(watchingItem);
  // The slide is for moving between the list and the watch view; on the
  // screen's own first paint there is nothing to move from.
  const isFirstRender = useIsFirstRender();

  // While watching, the header's back button returns to the list in place
  // (closing the watch view) rather than leaving the screen — otherwise
  // there'd be two back buttons doing different things (this one, and the
  // in-page one `CompilationWatchView` would render if `onBack` were passed).
  const watchHeaderLeft = useCallback(
    () => <HeaderBackButton onPress={closeWatchView} tintColor={theme.colors.text} />,
    [closeWatchView, theme.colors.text],
  );

  return (
    <>
      <Stack.Screen
        options={{
          title: "Clip Compilations",
          headerStyle: { backgroundColor: theme.colors.background },
          headerTintColor: theme.colors.text,
          headerBackButtonDisplayMode: "minimal",
          headerShadowVisible: false,
          headerLeft: watchingItem ? watchHeaderLeft : headerLeft,
        }}
      />
      {watchingItem ?
        <Animated.View
          key="watch"
          entering={isFirstRender ? undefined : SlideInRight}
          exiting={SlideOutRight}
          style={styles.flex}
        >
          <CompilationWatchView
            cameraLabel={watchingItem.cameraLabel}
            dateRange={`${watchingItem.rangeStart} – ${watchingItem.rangeEnd}`}
            onDownload={handleDownload}
          >
            <CompilationVideoPlayer clip={clip} maxHeight={screenHeight} />
          </CompilationWatchView>
        </Animated.View>
      : <Animated.View
          key="list"
          entering={isFirstRender ? undefined : SlideInLeft}
          exiting={SlideOutLeft}
          style={styles.flex}
        >
          <ClipCompilationPage {...pageProps} />
        </Animated.View>
      }
      <DialogModal visible={isRequestDialogOpen} onClose={closeRequestDialog} title="Request compilation">
        <RequestCompilationForm {...requestFormProps} />
      </DialogModal>
    </>
  );
}
